import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChevronRight, ExpandMore } from '@mui/icons-material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import {
  gray10,
  gray20,
  gray40,
  gray80,
  gray100,
  orange50,
  primaryButtonBackgroundColor,
  red20,
  red50,
  secondaryButtonBackgroundColor,
  tertiaryButtonBackgroundColor,
  white,
} from '../../../constants/colors';
import { boldMd, regularSm, regularXs } from '../../../constants/location';
import { MasterMappingType, MasterType } from '../../../types/master';
import {
  DependencyStep,
  resolveStepDependencies,
  StepDependency,
} from '../../../utils/master/resolveStepDependencies';
import CustomLoading from '../../uiParts/form/CustomLoading';
import MappingDetail from './MappingDetail';

const styles = {
  // マスタ一覧タブと同じ体裁に揃える（#3180）
  section: {
    backgroundColor: white,
    border: '1px solid' + gray40,
    borderRadius: '4px',
    boxSizing: 'border-box',
    margin: '0 32px',
    minHeight: '560px',
    padding: '16px 24px 56px',
    width: 'calc(100% - 64px)',
    '@media screen and (max-width:960px)': {
      border: 'none',
      borderRadius: 0,
      margin: 0,
      width: '100%',
    },
  },
  tableWrapper: {
    overflowX: 'auto',
    width: '100%',
    WebkitOverflowScrolling: 'touch',
  },
  table: {
    minWidth: '960px',
    overflowX: 'auto',
  },
  tableHeadRow: {
    height: '48px',
  },
  tableHeadText: {
    ...regularSm,
    color: gray80,
    padding: '0 16px',
    whiteSpace: 'nowrap',
  },
  patternRow: {
    height: '48px',
  },
  patternNameCell: {
    ...boldMd,
    color: gray100,
    padding: '0 16px',
  },
  // ステップ一覧の見出し（#3308）。マスタ一覧タブの「マスタ照合中のパターン」と
  // 同じ作りにして、タブ間で見え方を揃える。
  stepGroupHeaderRow: {
    backgroundColor: gray20,
    cursor: 'pointer',
    height: '36px',
    '&:hover': { backgroundColor: gray40 },
  },
  // 見出しはパターン名の配下なので、その内側へ寄せる（#3308）。
  // アイコンと文字は同じセルなので、左余白を足すと両方が一緒に動く。
  stepGroupHeaderCell: {
    borderBottom: `1px solid ${gray40}`,
    padding: '0 16px 0 48px',
  },
  stepGroupHeaderInner: {
    alignItems: 'center',
    display: 'flex',
    gap: '4px',
  },
  stepGroupHeaderText: {
    ...regularXs,
    color: gray80,
    fontWeight: 700,
    letterSpacing: '0.04em',
    whiteSpace: 'nowrap',
  },
  // 行クリックで開閉するため、アイコンは表示のみ。
  expandIconWrapper: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    padding: '4px',
  },
  stepRow: {
    backgroundColor: gray10,
  },
  // 見出し「設定中のマスタ照合フロー」の配下であることを示すため、一段インデントする（#3308）。
  stepCell: {
    ...regularSm,
    color: gray100,
    padding: '8px 16px 8px 92px',
    // 実行順の連結線をセルの高さいっぱいに引くための基準（#3308）
    position: 'relative',
  },
  // ドラッグハンドル・番号・マスタ名を上下中央で一直線に並べる（#3180）
  stepHeaderRow: {
    alignItems: 'center',
    display: 'flex',
    gap: '4px',
    marginBottom: '4px',
  },
  dragHandle: {
    cursor: 'grab',
    height: '24px',
    padding: 0,
    width: '24px',
  },
  // canReorder が false のときにハンドルぶんの幅を確保するための空要素。
  // uiParts の並び替えUIと同じ考え方（幅を空けて左端を揃える）。
  dragHandlePlaceholder: {
    flexShrink: 0,
    height: '24px',
    width: '24px',
  },
  // 実行順の番号バッジ（#3308）。オレンジ地に白抜きで「順序を表す記号」だと分かるようにする。
  stepOrderBadge: {
    alignItems: 'center',
    backgroundColor: orange50,
    borderRadius: '4px',
    color: white,
    display: 'flex',
    flexShrink: 0,
    fontSize: '12px',
    fontWeight: 700,
    height: '20px',
    justifyContent: 'center',
    // 幅は固定にする。桁数で広がると連結線との中心がずれるため（#3308）。
    // 12px 太字なら 2桁でも 20px に収まる。
    width: '20px',
  },
  /**
   * 次のステップへ向かう連結線（#3308）。
   *
   * バッジではなくセルを基準に描く。行の高さは内容（絞り込み・照合項目の行数）で
   * 変わるため、バッジ基準の固定値では下まで届かないため。
   * セル下端まで線を引き、先端に矢印を付けて「上から下へ実行される」ことを示す。
   * 最後のステップには付けない。
   */
  stepOrderConnector: {
    // 番号バッジの中心に合わせる。stepCell の左余白を変えたらここも追従させる。
    left: '130px',
    position: 'absolute',
    // バッジの下端から下へ。次の行に突き当てず、矢印の先が手前で止まるようにする。
    top: '30px',
    bottom: '10px',
    transform: 'translateX(-50%)',
    width: '2px',
    backgroundColor: gray40,
    // 先端の矢印。CSS三角形を線の下端に置く
    '&::after': {
      borderLeft: '4px solid transparent',
      borderRight: '4px solid transparent',
      borderTop: `6px solid ${gray40}`,
      bottom: '-6px',
      content: '""',
      height: 0,
      left: '50%',
      position: 'absolute',
      transform: 'translateX(-50%)',
      width: 0,
    },
  },
  stepMasterName: {
    ...boldMd,
    color: gray100,
    // 番号バッジとの間隔（#3308）。行の gap を広げるとバッジがハンドル側から離れ、
    // 連結線の基準位置（stepOrderConnector の left）ともずれるため、名前側で空ける。
    marginLeft: '8px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // 絞り込み・照合項目・取得項目の説明（#3308）。マスタ名に対する補足として一段下げる。
  stepDetail: {
    paddingLeft: '70px',
  },
  buttonCell: {
    padding: '8px 16px 8px 0',
    textAlign: 'right',
    verticalAlign: 'top',
    whiteSpace: 'nowrap',
  },
  buttonSmall: {
    fontSize: '13px',
    fontWeight: 700,
    height: '32px',
    minWidth: 'auto',
    padding: '0 12px',
  },
  icon18: {
    height: '18px',
    marginLeft: '0px',
    width: '18px',
  },
  footerRow: {
    backgroundColor: gray10,
  },
  // 実行順が依存関係と合っていないときの警告（#3180）。
  // 保存はブロックしないが、設定として機能しないことを明示する。
  orderAlert: {
    ...regularSm,
    backgroundColor: red20,
    borderRadius: '4px',
    color: red50,
    marginTop: '6px',
    padding: '6px 8px',
  },
  saveOrderButton: {
    ...primaryButtonBackgroundColor,
    fontSize: '13px',
    fontWeight: 700,
    height: '32px',
    minWidth: 'auto',
    padding: '0 16px',
  },
  emptyText: {
    ...regularSm,
    color: gray80,
    padding: '80px 0',
    textAlign: 'center',
  },
};

/** 1ステップ＝マッピング1件と、それが属するマスタの情報 */
export interface MappingStep {
  mapping: MasterMappingType;
  master: MasterType;
}

/** パターン1件と、そのパターンに紐づく照合ステップ（実行順に並んだ状態） */
export interface PatternMappingGroup {
  patternId: string;
  patternName: string;
  steps: MappingStep[];
}

interface StepRowProps {
  step: MappingStep;
  order: number;
  dependency: StepDependency;
  /** マスタ照合の編集・削除ができるか。マスタをアップロードできない企業でも可能（#3308） */
  canEditMapping: boolean;
  /** 実行順を並べ替えられるか。マスタのアップロード権限に連動する（#3308） */
  canReorder: boolean;
  /** 最後のステップか。番号から下へ伸びる連結線を出すかの判定に使う（#3308） */
  isLast: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

/** 照合ステップ1件の行。ドラッグで並べ替えられる。 */
const SortableStepRow = ({
  step,
  order,
  dependency,
  canEditMapping,
  canReorder,
  isLast,
  onEdit,
  onDelete,
}: StepRowProps): ReactElement => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: step.mapping.mapping_id,
  });

  // 実行順の番号は四角バッジと同じ素の数字で表記する（#3308）。
  // 複数あるとき「12」と繋がって読めてしまうため、読点で区切る。
  const formatOrders = (orders: number[]): string => orders.join('、');

  return (
    <TableRow
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition: isDragging ? undefined : transition,
        opacity: isDragging ? 0.5 : 1,
      }}
      sx={styles.stepRow}
    >
      <TableCell sx={styles.stepCell} colSpan={5}>
        {/* 次のステップへ向かう連結線（#3308）。番号バッジの真下からセル下端の手前まで
            引き、先端の矢印で「上から下へ実行される」ことを示す。 */}
        {!isLast && <Box sx={styles.stepOrderConnector} />}
        {/* ハンドル・番号・マスタ名は上下中央で一直線に並べる。
            ハンドルが動かすのは「ステップ1件」なので、行の左端ではなく
            対象であるステップの内容に隣接させる（#3308）。
            uiParts の並び替えUI（ChoiceEditor / ItemListManager）と同じ作法。 */}
        <Box sx={styles.stepHeaderRow}>
          {canReorder ? (
            <IconButton {...attributes} {...listeners} size="small" sx={styles.dragHandle}>
              <DragIndicatorIcon fontSize="small" />
            </IconButton>
          ) : (
            // 編集できないときも番号・マスタ名の左端がずれないよう場所だけ確保する
            <Box sx={styles.dragHandlePlaceholder} />
          )}
          {/* 実行順の番号（#3308）。丸数字（Unicode）は20までしか無く21件目から
              表記が崩れるため、四角のバッジを自前で描く。
              最後のステップ以外は、バッジから下へ線を伸ばして順序の連なりを見せる。 */}
          <Box sx={styles.stepOrderBadge}>{order}</Box>
          <Typography sx={styles.stepMasterName}>{step.master.file_name}</Typography>
        </Box>

        {/* 依存の注記は該当する絞り込み条件・照合項目の行にチップで出す（#3180）。
            どのキーが前段に依存しているかが行単位で分かるようにするため。
            順序が逆で機能しない場合の説明は、下部のアラートにまとめる。 */}
        <Box sx={styles.stepDetail}>
          <MappingDetail mapping={step.mapping} dependencyByItemId={dependency.byItemId} />

          {dependency.violated.length > 0 && (
            <Box sx={styles.orderAlert}>
              {formatOrders(dependency.violated.map(v => v.order))}
              の取得項目を使っていますが、まだ実行されていません。 この実行順序を
              {formatOrders(dependency.violated.map(v => v.order))}より後ろに移動してください。
            </Box>
          )}
        </Box>
      </TableCell>
      <TableCell sx={styles.buttonCell}>
        {canEditMapping && (
          <Box sx={{ alignItems: 'center', display: 'inline-flex', gap: '8px' }}>
            <Button onClick={onEdit} sx={[secondaryButtonBackgroundColor, styles.buttonSmall]}>
              編集
            </Button>
            <Button
              onClick={onDelete}
              startIcon={<DeleteOutline sx={styles.icon18} />}
              sx={[tertiaryButtonBackgroundColor, styles.buttonSmall]}
            >
              削除
            </Button>
          </Box>
        )}
      </TableCell>
    </TableRow>
  );
};

interface PatternGroupProps {
  group: PatternMappingGroup;
  /** ステップ一覧を展開しているか */
  expanded: boolean;
  /** マスタ照合の編集・削除ができるか（#3308） */
  canEditMapping: boolean;
  /** マスタ照合を追加できるか。アップロード権限に連動する（#3308） */
  canAddMapping: boolean;
  /** 実行順を並べ替えられるか（#3308） */
  canReorder: boolean;
  onToggleExpand: () => void;
  onDirtyChange: (patternId: string, dirty: boolean) => void;
  onAddStep: (patternId: string) => void;
  onEditStep: (step: MappingStep) => void;
  onDeleteStep: (step: MappingStep) => void;
  onSaveOrder: (patternId: string, orderedSteps: MappingStep[]) => Promise<void>;
}

/**
 * パターン1件分の行グループ（パターン行 ＋ そのパターンの照合ステップ行）。
 * 並べ替えはパターンごとに独立して行い、「実行順序を保存」で確定する。
 */
const PatternGroupRows = ({
  group,
  expanded,
  canEditMapping,
  canAddMapping,
  canReorder,
  onToggleExpand,
  onDirtyChange,
  onAddStep,
  onEditStep,
  onDeleteStep,
  onSaveOrder,
}: PatternGroupProps): ReactElement => {
  // 並べ替え中の表示順。保存するまで親には反映しない。
  const [orderedSteps, setOrderedSteps] = useState<MappingStep[]>(group.steps);
  const [saving, setSaving] = useState<boolean>(false);

  // 親のデータが変わったら編集中の並びを破棄して同期する（追加・削除・保存後の再取得）。
  useEffect(() => {
    setOrderedSteps(group.steps);
    onDirtyChange(group.patternId, false);
  }, [group.steps, group.patternId, onDirtyChange]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const dependencies = useMemo(
    () =>
      resolveStepDependencies(
        orderedSteps.map(
          (step): DependencyStep => ({
            mapping_id: step.mapping.mapping_id,
            search_item_id: step.mapping.search_item_id,
            search_item_label: step.mapping.search_item_label,
            search_csv_column: step.mapping.search_csv_column,
            match_mode: step.mapping.match_mode,
            search_key: step.mapping.search_key,
            filter_keys: step.mapping.filter_keys,
            column_mappings: step.mapping.column_mappings,
          }),
        ),
      ),
    [orderedSteps],
  );

  const isOrderChanged = orderedSteps.some(
    (step, index) => step.mapping.mapping_id !== group.steps[index]?.mapping.mapping_id,
  );

  const handleDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = orderedSteps.findIndex(s => s.mapping.mapping_id === active.id);
    const newIndex = orderedSteps.findIndex(s => s.mapping.mapping_id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const nextSteps = arrayMove(orderedSteps, oldIndex, newIndex);
    setOrderedSteps(nextSteps);
    // 並びが元に戻った場合は未保存ではない（#3308）。
    // 無条件に true を通知していたため、ドラッグで元の順序に戻しても親に未保存が残り、
    // 「実行順序を保存」ボタンが消えている（＝保存しようがない）のに離脱確認が出ていた。
    // isOrderChanged は再レンダー前の値なのでここでは使えず、新しい並びで比較する。
    const changed = nextSteps.some(
      (step, index) => step.mapping.mapping_id !== group.steps[index]?.mapping.mapping_id,
    );
    onDirtyChange(group.patternId, changed);
  };

  const handleSaveOrder = async (): Promise<void> => {
    setSaving(true);
    try {
      await onSaveOrder(group.patternId, orderedSteps);
      // 成功したときだけ未保存フラグを下ろす。失敗時に下ろすと、保存されていない
      // 並びが保存済み扱いになり離脱確認も出なくなる（#3308）。
      onDirtyChange(group.patternId, false);
    } catch {
      // エラー通知は呼び出し元（Master.tsx）がスナックバーで出すため、ここでは握るだけ。
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* 並べ替えハンドルはステップの内容に隣接させたため（#3308）、
          このタブには左端のハンドル列を置かない。 */}
      <TableRow sx={styles.patternRow}>
        <TableCell sx={styles.patternNameCell} colSpan={6}>
          {group.patternName || '未設定パターン'}
        </TableCell>
      </TableRow>

      {/* ステップ一覧の見出し（#3308）。開閉の有無にかかわらず常に出し、
          ここを開閉の起点にする（閉じているとき見出しごと消えると、
          開くためのボタンが無くなってしまうため）。 */}
      <TableRow sx={styles.stepGroupHeaderRow} onClick={onToggleExpand}>
        <TableCell colSpan={6} sx={styles.stepGroupHeaderCell}>
          <Box sx={styles.stepGroupHeaderInner}>
            {/* 開閉は行全体で受けるため、アイコンは見た目だけ（#3308）。 */}
            <Box sx={styles.expandIconWrapper}>{expanded ? <ExpandMore /> : <ChevronRight />}</Box>
            <Typography sx={styles.stepGroupHeaderText}>
              設定中のマスタ照合フロー（{orderedSteps.length}件）
            </Typography>
          </Box>
        </TableCell>
      </TableRow>

      {expanded && (
        <>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={orderedSteps.map(s => s.mapping.mapping_id)}
              strategy={verticalListSortingStrategy}
            >
              {orderedSteps.map((step, index) => (
                <SortableStepRow
                  key={step.mapping.mapping_id}
                  step={step}
                  order={index + 1}
                  dependency={dependencies[index]}
                  canEditMapping={canEditMapping}
                  canReorder={canReorder}
                  isLast={index === orderedSteps.length - 1}
                  onEdit={(): void => onEditStep(step)}
                  onDelete={(): void => onDeleteStep(step)}
                />
              ))}
            </SortableContext>
          </DndContext>

          {/* 出すものが無いときは行ごと消す（空の行が残ると何のための余白か分からないため）。
              追加はアップロード権限に連動し、運営のみアップロード可の企業には出さない。
              保存は並べ替えの確定操作なので並べ替え権限に連動する（#3308）。 */}
          {(canAddMapping || (canReorder && isOrderChanged)) && (
            <TableRow sx={styles.footerRow}>
              <TableCell colSpan={6} sx={styles.stepCell}>
                <Box sx={{ alignItems: 'center', display: 'flex', gap: '8px' }}>
                  {canAddMapping && (
                    <Button
                      startIcon={<AddCircleOutlineIcon />}
                      sx={[secondaryButtonBackgroundColor, styles.buttonSmall]}
                      onClick={(): void => onAddStep(group.patternId)}
                    >
                      マスタ照合を追加
                    </Button>
                  )}
                  {canReorder && isOrderChanged && (
                    <Button
                      sx={styles.saveOrderButton}
                      disabled={saving}
                      onClick={(): void => {
                        void handleSaveOrder();
                      }}
                    >
                      <Typography sx={{ ...boldMd, color: white, fontSize: '13px' }}>
                        実行順序を保存
                      </Typography>
                    </Button>
                  )}
                </Box>
              </TableCell>
            </TableRow>
          )}
        </>
      )}
    </>
  );
};

interface Props {
  /** パターンごとの照合ステップ。マスタ照合が1件以上あるパターンのみ */
  groups: PatternMappingGroup[];
  loading: boolean;
  /** マスタ照合の編集・削除・追加ができるか（#3308） */
  canEditMapping: boolean;
  /** マスタ照合を追加できるか。アップロード権限に連動する（#3308） */
  canAddMapping: boolean;
  /** 実行順を並べ替えられるか（#3308） */
  canReorder: boolean;
  /** 並べ替えの未保存状態を親へ伝える（タブ切り替え時の離脱確認に使う） */
  onDirtyChange: (patternId: string, dirty: boolean) => void;
  onAddStep: (patternId: string) => void;
  onEditStep: (step: MappingStep) => void;
  onDeleteStep: (step: MappingStep) => void;
  onSaveOrder: (patternId: string, orderedSteps: MappingStep[]) => Promise<void>;
}

/**
 * パターン別設定タブ（#3180）。
 *
 * パターンを主語にして、そのパターンに紐づく全マスタ照合を実行順に並べる。
 * マスタ一覧タブと同じテーブル体裁で、全パターンを一度に表示する。
 * ドラッグ&ドロップで並べ替え、「実行順序を保存」で確定する。
 * 並べ替えただけでは保存しないのは、誤操作でパターンの照合順が変わるのを防ぐため。
 */
const PatternMappingTab = ({
  groups,
  loading,
  canEditMapping,
  canAddMapping,
  canReorder,
  onDirtyChange,
  onAddStep,
  onEditStep,
  onDeleteStep,
  onSaveOrder,
}: Props): ReactElement => {
  // 折りたたみ中のパターンID。既定は全て展開（照合順の全体像が一目で分かるように）。
  // 「展開中」ではなく「折りたたみ中」を保持することで、パターンが増減しても
  // 既定の展開状態を保てる（新しく現れたパターンは未収録＝展開）。
  const [collapsedPatternIds, setCollapsedPatternIds] = useState<Set<string>>(new Set());

  const toggleExpand = (patternId: string): void => {
    setCollapsedPatternIds(prev => {
      const next = new Set(prev);
      if (next.has(patternId)) {
        next.delete(patternId);
      } else {
        next.add(patternId);
      }
      return next;
    });
  };

  const renderBody = (): ReactElement => {
    if (loading) return <CustomLoading loading={true} />;

    if (groups.length === 0) {
      return (
        <Typography sx={styles.emptyText}>
          マスタ照合が設定されているパターンがありません。
          <br />
          右上の「パターンにマスタ照合を設定する」から、読取パターンに使うマスタを設定してください。
        </Typography>
      );
    }

    return (
      <Box sx={styles.tableWrapper}>
        <Table sx={styles.table}>
          <TableHead>
            {/* 先頭列はマスタ一覧タブと同じ幅にして、
                「ファイル名」と「読取パターン」の左端を揃える（#3180）。 */}
            <TableRow sx={styles.tableHeadRow}>
              <TableCell sx={styles.tableHeadText} colSpan={6}>
                読取パターン
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.map(group => (
              <PatternGroupRows
                key={group.patternId}
                group={group}
                expanded={!collapsedPatternIds.has(group.patternId)}
                canEditMapping={canEditMapping}
                canAddMapping={canAddMapping}
                canReorder={canReorder}
                onToggleExpand={(): void => toggleExpand(group.patternId)}
                onDirtyChange={onDirtyChange}
                onAddStep={onAddStep}
                onEditStep={onEditStep}
                onDeleteStep={onDeleteStep}
                onSaveOrder={onSaveOrder}
              />
            ))}
          </TableBody>
        </Table>
      </Box>
    );
  };

  return <Box sx={styles.section}>{renderBody()}</Box>;
};

export default PatternMappingTab;
